/* Almila search field (snippets/almila-search): suggestions as you type from sections/almila-predictive-search,
   a combobox/listbox with arrow-key navigation, the mobile full-screen dialog (snippets/almila-search-dialog)
   and the results page sort control. Loaded by every field; defines everything once. */
(() => {
  if (customElements.get('almila-search')) return;

  const MIN_LENGTH = 2;
  const DELAY = 180;

  class AlmilaSearch extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.panel = this.querySelector('[data-search-panel]');
      this.status = this.querySelector('[data-search-status]');
      this.clearButton = this.querySelector('[data-search-clear]');
      this.empty = this.querySelector('template[data-search-empty]');
      this.sheet = this.dataset.layout === 'sheet';
      this.cache = new Map();
      this.options = [];
      this.active = -1;

      this.input.addEventListener('input', () => {
        this.syncClear();
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.update(), DELAY);
      });
      this.input.addEventListener('focus', () => this.update());
      this.input.addEventListener('keydown', event => this.onKeydown(event));
      this.clearButton?.addEventListener('click', () => {
        this.input.value = '';
        this.syncClear();
        this.input.focus();
        this.update();
      });
      this.form.addEventListener('submit', event => {
        if (!this.term) { event.preventDefault(); this.input.focus(); }
      });
      // Keep focus in the field while a suggestion is clicked (Safari does not focus links on click).
      this.panel.addEventListener('mousedown', event => event.preventDefault());
      this.addEventListener('focusout', event => {
        if (!this.sheet && !this.contains(event.relatedTarget)) this.close();
      });
    }

    get term() { return this.input.value.trim(); }

    syncClear() { if (this.clearButton) this.clearButton.hidden = !this.input.value; }

    async update() {
      const term = this.term;
      if (term.length < MIN_LENGTH) {
        this.controller?.abort();
        if (!term && this.empty) this.render(this.empty.innerHTML, '');
        else this.close();
        return;
      }
      if (this.cache.has(term)) { this.render(this.cache.get(term)); return; }
      this.controller?.abort();
      this.controller = new AbortController();
      const url = new URL(this.dataset.suggestUrl, location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('section_id', 'almila-predictive-search');
      url.searchParams.set('resources[type]', 'query,product,collection,page,article');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const results = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-search-results]');
        if (!results) throw new Error('no suggestions markup');
        this.cache.set(term, results.outerHTML);
        // Only the answer for what is in the field now is shown; slower, older answers are dropped. So is one that
        // arrives after focus has left the field (Tab held down through the header): it would open over the page.
        if (term === this.term && (this.sheet || this.contains(document.activeElement))) this.render(results.outerHTML);
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      }
    }

    render(html, summary) {
      this.panel.innerHTML = html;
      // Several fields share the suggestion markup; keep its ids unique per field.
      this.panel.querySelectorAll('[id]').forEach(element => { element.id = `${this.input.id}-${element.id}`; });
      this.panel.querySelectorAll('[aria-labelledby]').forEach(element => {
        element.setAttribute('aria-labelledby', `${this.input.id}-${element.getAttribute('aria-labelledby')}`);
      });
      this.options = [...this.panel.querySelectorAll('[role="option"]')];
      this.options.forEach((option, index) => {
        option.id = `${this.input.id}-option-${index}`;
        option.setAttribute('aria-selected', 'false');
        option.tabIndex = -1;
      });
      this.active = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
      const text = summary ?? this.panel.querySelector('[data-search-results]')?.dataset.summary ?? '';
      if (this.status.textContent !== text) this.status.textContent = text;
    }

    close() {
      if (this.sheet && this.empty) return;
      this.panel.hidden = true;
      this.panel.innerHTML = '';
      this.options = [];
      this.active = -1;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.classList.remove('is-open');
    }

    select(index) {
      if (!this.options.length) return;
      const count = this.options.length;
      this.active = (index + count) % count;
      this.options.forEach((option, i) => option.setAttribute('aria-selected', String(i === this.active)));
      const option = this.options[this.active];
      this.input.setAttribute('aria-activedescendant', option.id);
      option.scrollIntoView({ block: 'nearest' });
    }

    onKeydown(event) {
      if (event.key === 'Escape' && !this.sheet && !this.panel.hidden) {
        event.preventDefault();
        event.stopPropagation();
        this.close();
        return;
      }
      if (this.panel.hidden || !this.options.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.select(this.active + (event.key === 'ArrowDown' ? 1 : -1));
      } else if (event.key === 'Enter' && this.active >= 0) {
        event.preventDefault();
        this.options[this.active].click();
      }
    }
  }
  customElements.define('almila-search', AlmilaSearch);

  // Mobile: the header button opens the search as a full-screen dialog.
  const root = document.documentElement;
  document.addEventListener('click', event => {
    const opener = event.target.closest('[data-search-open]');
    if (opener) {
      const dialog = document.getElementById(opener.getAttribute('aria-controls'));
      if (!dialog || dialog.open) return;
      dialog.showModal();
      root.classList.add('almila-search-locked');
      const search = dialog.querySelector('almila-search');
      search?.input.focus();
      search?.update();
      return;
    }
    if (event.target.closest('[data-search-close]')) event.target.closest('dialog')?.close();
  });
  document.addEventListener('close', event => {
    if (event.target.matches?.('.almila-search-dialog')) root.classList.remove('almila-search-locked');
  }, true);

  // Results page: sorting applies at once; the submit button is the fallback without JavaScript.
  document.querySelectorAll('[data-search-sort-apply]').forEach(button => { button.hidden = true; });
  document.addEventListener('change', event => {
    if (event.target.matches('[data-search-sort]')) event.target.form.requestSubmit();
  });
})();
